import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Boxes, Plus, KeyRound, Smartphone, ShieldCheck, Users, Loader2, ChevronRight, AlertTriangle } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { productsService, Product } from '@/services/products.service';
import { whatsappAccountService, WhatsappAccount } from '@/services/whatsapp-account.service';
import { CredentialsVault } from '@/components/products/CredentialsVault';
import { FEATURES, Spark, initials, relative } from './productShared';

export default function ProductsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<Product[] | null>(null);
  const [accounts, setAccounts] = useState<WhatsappAccount[]>([]);

  const load = () => productsService.list().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); whatsappAccountService.list().then(setAccounts).catch(() => setAccounts([])); }, []);
  const accountById = useMemo(() => new Map(accounts.map((a) => [a.accountUid, a])), [accounts]);

  return (
    <>
      <Topbar
        title="Products"
        breadcrumb="Products"
        description="Your own apps, linked to this company's WhatsApp numbers, templates and keys."
        right={<Button onClick={() => nav('/products/new')} iconLeft={<Plus size={14} />}>Add product</Button>}
      />
      <div className="space-y-6 px-7 py-6">
        {items === null ? (
          <div className="flex items-center gap-2 py-10 text-sm text-text-muted"><Loader2 size={15} className="animate-spin" /> Loading products…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Boxes size={28} />}
            title="No products linked yet"
            description="Add PrepMDCAT, PrepFCPS or any app you own. Pick the WhatsApp number and OTP template it sends from, choose which keys it receives, then issue it an API key."
            action={<Button onClick={() => nav('/products/new')} iconLeft={<Plus size={14} />}>Add product</Button>}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {items.map((p) => {
              const acc = accountById.get(p.whatsappAccountUid);
              const s = p.stats;
              const keys = p.credentialUids?.length ?? 0;
              return (
                <Link
                  key={p.productUid}
                  to={`/products/${p.productUid}`}
                  className={clsx(
                    'group flex flex-col rounded-2xl border bg-surface p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-md',
                    p.isActive ? 'border-border hover:border-primary/60' : 'border-dashed border-border opacity-70',
                  )}
                >
                  <div className="flex items-start gap-3">
                    {p.iconUrl
                      ? <img src={p.iconUrl} alt="" className="h-12 w-12 rounded-xl object-cover" />
                      : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-sm font-bold text-primary">{initials(p.name)}</span>}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-base font-semibold text-text-primary group-hover:text-primary">{p.name}</h3>
                        {!p.isActive && <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-2xs text-text-muted">Paused</span>}
                        {p.aiEnabled === false && <span className="rounded-full bg-danger-soft px-1.5 py-0.5 text-2xs font-semibold text-danger">AI off</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {p.features.map((f) => {
                          const def = FEATURES.find((x) => x.id === f);
                          return <span key={f} className="inline-flex items-center gap-1 rounded-full bg-primary-soft/60 px-2 py-0.5 text-2xs font-medium text-primary">{def?.icon}{def?.label ?? f}</span>;
                        })}
                      </div>
                    </div>
                    <ChevronRight size={16} className="mt-1 shrink-0 text-text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>

                  <ul className="mt-4 space-y-1.5 rounded-xl border border-primary/20 bg-primary-soft/30 px-3 py-2.5 text-xs">
                    <li className="flex items-center gap-2">
                      <Smartphone size={12} className="shrink-0 text-primary" />
                      {acc
                        ? <span className="truncate text-text-primary">{acc.verifiedName || acc.businessName} <span className="text-text-muted">· {acc.displayPhoneNumber}</span></span>
                        : <span className="inline-flex items-center gap-1 text-warning"><AlertTriangle size={11} /> WhatsApp account missing</span>}
                    </li>
                    <li className="flex items-center gap-2">
                      <ShieldCheck size={12} className="shrink-0 text-primary" />
                      <span className="truncate text-text-primary">{p.otpTemplateName} <span className="text-text-muted">· {p.otpTemplateLanguage}</span></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <KeyRound size={12} className="shrink-0 text-primary" />
                      <span className={clsx('truncate', keys ? 'text-text-primary' : 'text-warning')}>
                        {keys ? `${keys} key${keys === 1 ? '' : 's'} delivered` : 'No keys assigned'}
                        <span className="text-text-muted"> · {p.activeKeys ? `${p.activeKeys} API key${p.activeKeys === 1 ? '' : 's'}` : 'no API key yet'}</span>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Users size={12} className="shrink-0 text-primary" />
                      <span className="text-text-primary">{(s?.contacts ?? 0).toLocaleString()} <span className="text-text-muted">synced contacts</span></span>
                    </li>
                  </ul>

                  <div className="mt-4 flex items-end justify-between rounded-xl bg-surface-alt/50 px-3 py-2.5">
                    <div className="flex gap-5">
                      <Stat label="OTPs · 30d" value={s?.otpSent30d ?? 0} />
                      <Stat label="Messages" value={s?.transactionalSent30d ?? 0} />
                      <Stat label="Failed" value={s?.failed30d ?? 0} danger={(s?.failed30d ?? 0) > 0} />
                    </div>
                    <div className="text-right">
                      <Spark data={s?.spark ?? []} width={96} height={24} />
                      <div className="text-2xs text-text-muted">last send {relative(s?.lastSentAt)}</div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <CredentialsVault onChanged={load} />
      </div>
    </>
  );
}

function Stat({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div>
      <div className={clsx('text-base font-semibold tabular-nums leading-none', danger ? 'text-danger' : 'text-text-primary')}>{value.toLocaleString()}</div>
      <div className="mt-0.5 text-2xs text-text-muted">{label}</div>
    </div>
  );
}
